/**
 *
 * useMainStore - Infrastructure and Cache Layer
 *
 * This store serves as the shared infrastructure layer for all 13 domain stores.
 * It provides centralized services, page cache management, and loading state coordination.
 *
 * PRIMARY RESPONSIBILITIES:
 * 1. Shared State Management Services - Used by all domain stores:
 *    - updatePage(data) - Updates page cache
 *    - updateLoading({ key, status }) - Manages loading states
 *    - updateResults({ key, status }) - Manages result states
 *    - updatePagination(data) - Handles pagination
 *    - resetPageData(payload) - Resets page cache
 *
 * 2. Centralized Cache Layer - The `page` object stores:
 *    - Viewed entities (employee, organization, request, opportunity)
 *    - Paginated results (employees, requests, organizations, templates)
 *    - Derived data (applicants, applications, matches)
 *    - Pagination metadata, applied filters, error state
 *
 * 3. Loading State Manager - The `status` object centralizes loading states for:
 *    - User operations (user, employee_activate, org_activate)
 *    - Organization operations (organization, invites, list_employees)
 *    - Request operations (requests, request_apply, request_submit, etc.)
 *    - Page-level loading (page, app)
 *    - Schedule operations (schedules)
 *
 * 4. Shared User State - The `user` object holds:
 *    - employeeId - Set by authGuard, accessed by multiple stores
 *    - data - Shared between employeeStore and educatorStore (transitional architecture)
 *
 * DEPENDENCIES:
 * Used by 13 domain stores:
 * - useAppStore, useModalStore, useNotificationStore, useAttachmentStore
 * - useOrganizationStore, useEmployeeStore, useEducatorStore
 * - useRequestStore, useRequestTemplateStore, useOpportunityStore
 * - useMessagingStore, useResourceStore, useSettingsStore
 *
 * Used by 40+ components for accessing page cache and loading states.
 */

import { defineStore } from 'pinia';
import { useAppStore } from '@/stores/useAppStore';

/**
 * Page Cache Schema
 * Defines the structure of cached data stored in the page object.
 * This cache is populated by domain stores and accessed by components.
 */
const pageSchema = {
    // Browse/Employee data
    employees: [], // Cache for getEmployees/getRecommendedEmployees (populated by employeeStore)
    availableFilters: [], // Cache for available filter options
    industries: [], // Cache for industry/subcategory data from /industries endpoint (populated by employeeStore)

    // Organization data
    organization: {}, // Cache for viewOrganization (populated by organizationStore)
    organizations: [], // Cache for getOrganizations (populated by organizationStore)

    // Employee view data
    employee: {}, // Cache for viewEmployee (populated by employeeStore)

    // Request data
    request: {}, // Cache for current request view
    requests: [], // Cache for getRequests (populated by requestStore)
    requests_length: null, // Cache for getRequests (populated by requestStore)
    applicants: [], // Cache for request applicants
    applications: [], // Cache for employee applications
    recommended: [], // Cache for recommended requests
    request_matches: [], // Cache for request matches
    direct_requested_applicant: null, // Cache for direct request applicant
    accepted_chat_id: null, // Cache for accepted chat ID
    upcoming: [], // Cache for upcoming requests
    appliedFilters: null, // Cache for applied filters

    // Settings data
    settings: {}, // Cache for settings (populated by settingsStore)

    // General
    error: null, // Page-level error state
    params: null, // URL/query parameters
};

// Helper functions

export const useMainStore = defineStore('main', {
    state: () => ({
        app: {},
        page: {
            ...pageSchema,
            pagination: {},
            params: {},
        },
        user: {
            // employeeId will still be set by authGuard
            // Other employee properties moved to useEmployeeStore
            data: {}, // Initialize data as empty object for educator/employee data
            organizations: [], // User's organizations
        },
        status: {
            user: {
                loading: false,
                error: null,
            },
            organization: {
                loading: false,
                error: null,
            },
            schedules: {
                loading: false,
                error: null,
            },
            invites: {
                loading: false,
                error: null,
            },
            employee_activate: {
                loading: false,
                error: null,
            },
            org_activate: {
                loading: false,
                error: null,
            },
            list_employees: {
                loading: false,
                error: null,
            },
            page: {
                loading: false,
                error: null,
            },
            app: {
                loading: false,
                error: null,
            },
            requests: {
                loading: false,
                error: null,
            },
            request_apply: {
                loading: false,
                results: null,
                error: null,
            },
            request_submit: {
                loading: false,
                results: null,
                error: null,
            },
            request_save_draft: {
                loading: false,
                results: null,
                error: null,
            },
            request_direct_submit: {
                loading: false,
                results: null,
                error: null,
            },
            request_direct_accept: {
                loading: false,
                results: null,
                error: null,
            },
            application_accept: {
                loading: false,
                results: null,
                error: null,
            },
            request_direct_decline: {
                loading: false,
                error: null,
            },
            industries: {
                loading: false,
                error: null,
            },
            organization_save: {
                loading: false,
                error: null,
            },
            organization_employee_remove: {
                loading: false,
                error: null,
            },
            organization_avatar: {
                loading: false,
                error: null,
            },
            organizations_list: {
                loading: false,
                error: null,
            },
            organization_employees_export: {
                loading: false,
                error: null,
            },
            groups: {
                loading: false,
                error: null,
            },
            groups_save: {
                loading: false,
                error: null,
            },
            groups_edit: {
                loading: false,
                error: null,
            },
            groups_delete: {
                loading: false,
                error: null,
            },
        },
    }),

    getters: {
        request(state) {
            return state.page.request;
        },
        applyResult(state) {
            return state.status.request_apply.results;
        },
        acceptDirectRequestResult(state) {
            return state.status.request_direct_accept.results;
        },
        applicationAccept(state) {
            return state.status.application_accept.results;
        },
        settings(state) {
            return state.page.settings;
        },
        params(state) {
            return state.page.params;
        },
    },

    actions: {
        /**
         * updateApp - Delegates app-level properties to useAppStore
         * Used during initialization to route app-level state to the correct store.
         * @param {Object} data - Properties to update
         */
        updateApp(data) {
            // Delegate app-level properties to useAppStore
            const appLevelKeys = ['mode', 'flags', 'error', 'active', 'spotlight', 'queue', 'breadcrumb'];
            const appLevelData = {};
            const mainStoreData = {};

            Object.keys(data).forEach((key) => {
                if (appLevelKeys.includes(key)) {
                    appLevelData[key] = data[key];
                } else {
                    mainStoreData[key] = data[key];
                }
            });

            // Update app store with app-level properties
            if (Object.keys(appLevelData).length > 0) {
                const appStore = useAppStore();
                appStore.updateApp(appLevelData);
            }

            // Update main store with remaining properties
            Object.keys(mainStoreData).forEach((key) => {
                this.app[key] = mainStoreData[key];
            });
        },

        /**
         * updatePage - Updates page cache with new data
         * Used by all 13 domain stores to cache fetched data.
         * Used by: organizationStore, employeeStore, requestStore, settingsStore, etc.
         * @param {Object} data - Data to cache (can include 'key' for nested updates)
         */
        updatePage(data) {
            if (data.key) {
                const { key, ...rest } = data;
                if (!key) {
                    return;
                }
                this.page[key] = { ...this.page[key], ...rest };
            } else {
                // Update properties directly instead of recreating entire page object
                Object.keys(data).forEach((key) => {
                    this.page[key] = data[key];
                });
            }
        },

        /**
         * updatePageKey - Updates a specific key in page cache
         * Similar to updatePage but always requires a key parameter.
         * @param {Object} data - Must include 'key' and properties to update
         */
        updatePageKey(data) {
            const { key, ...rest } = data;
            if (!key) {
                return;
            }
            this.page[key] = { ...this.page[key], ...rest };
        },

        /**
         * updateLoading - Manages loading states for operations
         * Used by 10+ stores to centralize loading state management.
         * Used by: organizationStore, employeeStore, requestStore, etc.
         * @param {Object} params - { key: string, status: boolean }
         */
        updateLoading({ key, status }) {
            // Initialize the status key if it doesn't exist
            if (!this.status[key]) {
                this.status[key] = { loading: false, error: null };
            }
            this.status[key].loading = status;
        },

        /**
         * updateResults - Manages result states for operations
         * Used by requestStore to store operation results.
         * @param {Object} params - { key: string, status: any }
         */
        updateResults({ key, status }) {
            // Initialize the status key if it doesn't exist
            if (!this.status[key]) {
                this.status[key] = { loading: false, error: null };
            }
            this.status[key].results = status;
        },

        /**
         * resetPageData - Resets page cache to initial state
         * Used by 8+ components during cleanup/navigation.
         * Used by: RequestDirectory, EmployeeProfilePublic, Message, etc.
         * @param {Object} payload - Optional { key: string } to reset specific cache key
         */
        async resetPageData(payload) {
            if (payload && payload.key) {
                this.page = { ...this.page, [payload.key]: pageSchema[payload.key] };
            } else {
                this.page = { ...pageSchema, pagination: {}, params: {} };
            }
        },

        /**
         * updateUserData - Updates user.data object
         * Used by educatorStore and employeeStore to update user data.
         * @param {Object} data - Properties to update in user.data
         * @returns {Object} The updated user.data object
         */
        updateUserData(data) {
            this.user.data = { ...this.user.data, ...data };
            return this.user.data;
        },

        /**
         * updatePagination - Handles pagination metadata
         * Used by 5+ stores to centralize pagination state.
         * Used by: requestStore, requestTemplateStore, organizationStore, employeeStore.
         * @param {Object} data - { record_count, limit, offset, records or recordsLength }
         */
        updatePagination(data) {
            let page;
            const recordsLength = data.records ? data.records.length : data.recordsLength;
            const { record_count: recordCount, limit, offset } = data;

            if (offset === 0) {
                page = 1;
            } else {
                page = Math.floor(offset / limit) + 1;
            }

            const newPagination = {
                page,
                recordCount,
                limit,
                offset,
                pageCount: recordsLength,
            };

            // Only update if values changed
            const currentPagination = this.page.pagination;
            if (currentPagination) {
                const hasChanged =
                    currentPagination.page !== newPagination.page ||
                    currentPagination.recordCount !== newPagination.recordCount ||
                    currentPagination.limit !== newPagination.limit ||
                    currentPagination.offset !== newPagination.offset ||
                    currentPagination.pageCount !== newPagination.pageCount;

                if (!hasChanged) {
                    return;
                }
            }

            this.updatePage({
                pagination: newPagination,
            });
        },
    },
});
